import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, StatusBadge, money, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { MonthGrid } from "@/components/schedule/MonthGrid";
import { format, parseISO, addMonths } from "date-fns";
import { List, CalendarDays, ChevronLeft, ChevronRight, ArrowLeft, Plus, FileText } from "lucide-react";

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [view, setView] = useState("list");
  const [monthDate, setMonthDate] = useState(new Date());

  useEffect(() => {
    api.get(`/customers/${id}`).then((r) => setClient(r.data)).catch(() => setClient(null));
    api.get("/jobs").then((r) => {
      const mine = r.data.filter((j) => j.customer_id === id);
      setJobs(mine);
      if (mine.length) {
        const now = Date.now();
        const nearest = mine.reduce((a, b) =>
          Math.abs(new Date(a.scheduled_date) - now) < Math.abs(new Date(b.scheduled_date) - now) ? a : b
        );
        setMonthDate(parseISO(nearest.scheduled_date));
      }
    }).catch(() => {});
  }, [id]);

  const sorted = useMemo(
    () => [...jobs].sort((a, b) => (b.scheduled_date || "").localeCompare(a.scheduled_date || "")),
    [jobs]
  );

  const openEdit = (j) => navigate(`/jobs?job=${j.id}`);

  return (
    <div>
      <button
        onClick={() => navigate("/customers")}
        data-testid="back-to-clients"
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-brand mb-4 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Clients
      </button>

      <PageHeader
        eyebrow="Client"
        title={client?.name || "Client"}
        description={[client?.suburb, client?.postcode].filter(Boolean).join(" ") || client?.email || ""}
        action={
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => navigate(`/reports?client=${id}`)} data-testid="client-tax-report" className="rounded-sm gap-2">
              <FileText className="w-4 h-4" /> Tax report
            </Button>
            <Button onClick={() => navigate(`/jobs?client=${id}`)} data-testid="new-booking-for-client" className="rounded-sm gap-2">
              <Plus className="w-4 h-4" /> New Booking
            </Button>
          </div>
        }
      />

      <div className="flex items-center justify-between mb-6">
        <div className="inline-flex border border-border/60 rounded-sm overflow-hidden">
          <button
            onClick={() => setView("list")}
            data-testid="client-view-list"
            className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${view === "list" ? "bg-[hsl(330_85%_93%)] grad-ink" : "text-muted-foreground hover:bg-accent/60"}`}
          >
            <List className="w-4 h-4" /> <span className="gi-text">List</span>
          </button>
          <button
            onClick={() => setView("calendar")}
            data-testid="client-view-calendar"
            className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${view === "calendar" ? "bg-[hsl(330_85%_93%)] grad-ink" : "text-muted-foreground hover:bg-accent/60"}`}
          >
            <CalendarDays className="w-4 h-4" /> <span className="gi-text">Calendar</span>
          </button>
        </div>
        <div className="text-sm text-muted-foreground" data-testid="client-booking-count">
          {jobs.length} booking{jobs.length === 1 ? "" : "s"}
        </div>
      </div>

      {jobs.length === 0 ? (
        <EmptyState
          title="No bookings yet"
          description={`${client?.name || "This client"} has no bookings. Create one to get started.`}
          action={<Button onClick={() => navigate(`/jobs?client=${id}`)} className="rounded-sm">New Booking</Button>}
        />
      ) : view === "list" ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="label-caps">Date</TableHead>
              <TableHead className="label-caps">Time</TableHead>
              <TableHead className="label-caps">Package</TableHead>
              <TableHead className="label-caps">Status</TableHead>
              <TableHead className="text-right label-caps">Price</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((j) => (
              <TableRow key={j.id} data-testid={`client-job-${j.id}`} className="cursor-pointer" onClick={() => openEdit(j)}>
                <TableCell className="py-4 font-medium">
                  {j.scheduled_date ? format(parseISO(j.scheduled_date), "EEE d MMM yyyy") : "—"}
                </TableCell>
                <TableCell>{j.scheduled_date ? format(parseISO(j.scheduled_date), "h:mma") : "—"}</TableCell>
                <TableCell>{j.package?.name || "Clean"}</TableCell>
                <TableCell><StatusBadge status={j.status} /></TableCell>
                <TableCell className="text-right">{money(j.price || 0)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => setMonthDate((d) => addMonths(d, -1))} data-testid="client-month-prev" className="p-2 hover:text-brand text-muted-foreground">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="font-serif text-2xl">{format(monthDate, "MMMM yyyy")}</div>
            <button onClick={() => setMonthDate((d) => addMonths(d, 1))} data-testid="client-month-next" className="p-2 hover:text-brand text-muted-foreground">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
          <MonthGrid
            monthDate={monthDate}
            jobs={jobs}
            onDayClick={() => {}}
            onEventClick={(j) => openEdit(j)}
          />
        </div>
      )}
    </div>
  );
}
